<template lang="pug">
el-dialog(
  :title="order ? `Rastreamento do pedido #${order.id}` : 'Rastreamento'",
  :visible="visible",
  @update:visible="emit('update:visible', $event)",
  width="50%"
)
  .order-tracking(v-if="order", v-loading="loading")
    el-descriptions(:column="1", border, size="small")
      el-descriptions-item(label="Transportadora") {{ order.shippingCompany || '-' }}
      el-descriptions-item(label="Código de rastreamento") {{ order.trackingCode }}
      el-descriptions-item(v-if="tracking", label="Status do pedido")
        | {{ statusTranslation[tracking.status] || tracking.status }}
      el-descriptions-item(v-if="order.trackingUrl", label="Link para o cliente")
        el-input(:value="order.trackingUrl", size="small", readonly)
          el-button(slot="append", title="Copiar link", @click="copyCustomerLink")
            font-awesome-icon(:icon="['far', 'copy']")

    el-timeline.timeline(v-if="tracking && tracking.events.length > 0")
      el-timeline-item(
        v-for="(event, index) in tracking.events",
        :key="index",
        :timestamp="formatInstant(event.instant)",
        placement="top"
      )
        .event__description {{ event.description }}
        .event__detail(v-if="event.detail") {{ event.detail }}
        .event__detail(v-if="event.origin") De: {{ formatLocation(event.origin) }}
        .event__detail(v-if="event.destination") Para: {{ formatLocation(event.destination) }}

    p.empty(v-else-if="!loading") {{ errorMessage || 'Nenhum evento de rastreamento encontrado.' }}

  span(slot="footer")
    el-link(
      v-if="order && carrierTrackingUrl(order)",
      :href="carrierTrackingUrl(order)",
      target="_blank",
      rel="noopener noreferrer",
      type="primary"
    ) Abrir no site da transportadora

</template>
<script setup>
import moment from 'moment'
import { ref, watch } from 'vue'
import { useMessage } from '@/utils/Message'

import SubscriptionBoxOrderService from '@/services/fulfillment/SubscriptionBoxOrderService'

const message = useMessage()

const props = defineProps({
  order: {
    type: Object,
    default: null
  },
  visible: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update:visible'])

const statusTranslation = SubscriptionBoxOrderService.statusTranslation

const loading = ref(false)
const tracking = ref(null)
const errorMessage = ref(null)

const loadTracking = async () => {
  const orderId = props.order.id

  loading.value = true
  tracking.value = null
  errorMessage.value = null

  try {
    const result = await SubscriptionBoxOrderService.getTracking(orderId)

    // Ignore responses for an order the dialog is no longer showing
    if (props.order?.id === orderId) {
      tracking.value = result
    }
  } catch (e) {
    if (props.order?.id === orderId) {
      errorMessage.value = e.status === 404
        ? 'Nenhum rastreamento disponível para este pedido.'
        : 'Não foi possível consultar a transportadora. Tente novamente ou use o site da transportadora.'
    }
  } finally {
    if (props.order?.id === orderId) {
      loading.value = false
    }
  }
}

const copyCustomerLink = async () => {
  try {
    await navigator.clipboard.writeText(props.order.trackingUrl)
    message.success('Link copiado!')
  } catch (e) {
    message.error('Falha ao copiar.')
  }
}

const formatInstant = (instant) => moment(instant).format('DD/MM/YYYY HH:mm')

const formatLocation = (location) => {
  const cityAndState = [location.city, location.state].filter(Boolean).join('/')

  return [location.location, cityAndState].filter(Boolean).join(' - ')
}

const carrierTrackingUrl = (order) => {
  switch (order.shippingCompany) {
    case 'CARRIERS':
      return `https://www.carriers.com.br/portal/localizador.php?l=${order.trackingCode}`
    case 'TOTAL_EXPRESS':
      return `https://tracking.totalexpress.com.br/poupup_track.php?reid=14252&pedido=${order.nfe}&nfiscal=${order.nfe}`
    case 'LOGGI':
      return `https://www.loggi.com/rastreador/${order.trackingCode}`
    case 'MANDAE':
      return `https://rastreae.com.br/resultado/${order.trackingCode}`
    case 'SMARTENVIOS':
      return `https://portal.smartenvios.com/rastreamento/codigo-de-rastreio/${order.trackingCode}`
    default:
      return `https://rastreamento.correios.com.br/app/index.php?objetos=${order.trackingCode}`
  }
}

watch(() => props.visible, (newValue) => {
  if (newValue && props.order) {
    loadTracking()
  }
})
</script>

<style lang="scss" scoped>
.order-tracking {
  font-size: 14px;
  min-height: 120px;
  text-align: left;

  .timeline {
    margin-top: 24px;
    padding-left: 4px;
  }

  .event__description {
    font-weight: 500;
  }

  .event__detail {
    margin-top: 4px;
    color: #909399;
  }

  .empty {
    margin-top: 24px;
    text-align: center;
    color: #909399;
  }
}
</style>
